/**
 * Meta-style date-range presets for the ad-spend views.
 * All ranges are computed in local time and returned as `YYYY-MM-DD` strings
 * (the format the Facebook Graph API's `time_range` expects).
 */
export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'last7'
  | 'last14'
  | 'last28'
  | 'last30'
  | 'this_week'
  | 'this_month'
  | 'this_quarter'
  | 'this_year'
  | 'custom';

export interface DateRange {
  from: string;
  to: string;
}

/** Local `YYYY-MM-DD`. */
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Options for a range dropdown (order matches Meta's menu). */
export const RANGE_OPTIONS: Array<{ value: RangePreset; label: string }> = [
  { value: 'today', label: 'Hôm nay' },
  { value: 'yesterday', label: 'Hôm qua' },
  { value: 'last7', label: '7 ngày qua' },
  { value: 'last14', label: '14 ngày qua' },
  { value: 'last28', label: '28 ngày qua' },
  { value: 'last30', label: '30 ngày qua' },
  { value: 'this_week', label: 'Tuần này' },
  { value: 'this_month', label: 'Tháng này' },
  { value: 'this_quarter', label: 'Quý này' },
  { value: 'this_year', label: 'Năm nay' },
  { value: 'custom', label: 'Tùy chỉnh' },
];

/** Resolve a preset into a concrete `{ from, to }` range (local time). */
export function presetToRange(preset: RangePreset, now: Date = new Date()): DateRange {
  const today = ymd(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = addDays(now, -1);
      return { from: ymd(y), to: ymd(y) };
    }
    case 'last7':
      return { from: ymd(addDays(now, -6)), to: today };
    case 'last14':
      return { from: ymd(addDays(now, -13)), to: today };
    case 'last28':
      return { from: ymd(addDays(now, -27)), to: today };
    case 'last30':
      return { from: ymd(addDays(now, -29)), to: today };
    case 'this_week': {
      // Week starts Monday.
      const dow = (now.getDay() + 6) % 7; // 0 = Monday
      return { from: ymd(addDays(now, -dow)), to: today };
    }
    case 'this_month':
      return { from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      return { from: ymd(new Date(now.getFullYear(), q * 3, 1)), to: today };
    }
    case 'this_year':
      return { from: ymd(new Date(now.getFullYear(), 0, 1)), to: today };
    default:
      return { from: ymd(addDays(now, -29)), to: today };
  }
}
